import renderInputField from 'components/FormControls/textInput'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Field, reduxForm } from 'redux-form'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import baseLoginValidator from 'utils/validators/baseLoginValidator'
import * as Yup from 'yup'
import LoginField from '../subcomponents/LoginField'
import { useAccountCreateFormSubmit } from 'modules/Forms/Account/Create/useAccountCreateFormSubmit'
import FormFrame from 'modules/Forms/Account/Create/subcomponents/FormFrame'
import ProviderFields from 'modules/Forms/Account/Create/subcomponents/ProviderFields'
import omit from 'lodash/omit'
import urlValidator from 'utils/validators/urlValidator'
import { onlyNumbers } from 'utils/normalize/onlyNumbers'
import ProviderNotification from 'modules/Forms/Account/Create/subcomponents/ProviderNotification'
import clsx from 'clsx'
import formStyles from 'modules/Forms/common-form.module.scss'
import emailValidator from 'utils/validators/emailValidator'

export const FacebookHighRiskSchema = () =>
  Yup.object().shape({
    network: Yup.string().required('common::validation_messages.required'),
    logins: Yup.array().of(Yup.object()),
    login: Yup.string().when('logins', ([logins], schema) =>
      logins
        ? schema.strip()
        : schema
            .test('baseLoginTest', '', baseLoginValidator)
            .required('common::validation_messages.required'),
    ),
    provider_id: Yup.string().required('common::validation_messages.required'),
    product_url: Yup.string()
      .test('urlValidator', 'common::validation_messages.incorrect_url_format', urlValidator)
      .required('common::validation_messages.required'),
    creative_url: Yup.string().test(
      'urlValidator',
      'common::validation_messages.incorrect_url_format',
      urlValidator,
    ),
    cloaking_url: Yup.string().test(
      'urlValidator',
      'common::validation_messages.incorrect_url_format',
      urlValidator,
    ),
    fan_page_id: Yup.string(),
    business_manager_id: Yup.string(),
    email: Yup.string()
      .test('emailValidator', 'common::validation_messages.incorrect_email_format', emailValidator)
      .required('common::validation_messages.required'),
  })

let CreateFacebookHighRiskForm = ({
  handleSubmit,
  submitting,
  onClose,
  autofill,
  form,
  networkId,
  array,
  multiLogins,
  networkType,
}) => {
  const [currentProvider, setCurrentProvider] = useState(null)
  const { t } = useTranslation()
  const submitAccountData = useAccountCreateFormSubmit({ form, onClose, multiLogins, networkType })

  useEffect(() => {
    if (networkId) {
      autofill('network', networkId)
    }
  }, [networkId])

  const submit = useCallback(
    (values) => submitAccountData(omit(values, ['geo', 'currency'])),
    [submitAccountData],
  )

  return (
    <FormFrame
      onSubmit={handleSubmit(submit)}
      multiLogins={multiLogins}
      form={form}
      submitAccountData={submitAccountData}
      submitting={submitting}
      onClose={onClose}
    >
        <Field
          disabled
          name="network"
          hidden
          fullWidth
          component={renderInputField}
          t={t}
        />

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <LoginField
            form={form}
            multiLogins={multiLogins}
            array={array}
            autofill={autofill}
            networkId={networkId}
            label={'services::forms.register_account.facebook.fields.login.label'}
          />
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="email"
            type="email"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.facebook-high-risk.fields.email.label')}
            t={t}
          />
        </div>
        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="product_url"
            type="url"
            fullWidth
            component={renderInputField}
            label={t(
              'services::forms.register_account.facebook-high-risk.fields.product_url.label',
            )}
            t={t}
          />
        </div>

        <ProviderFields
          autofill={autofill}
          form={form}
          networkId={networkId}
          onProviderChange={setCurrentProvider}
          networkType={networkType}
        />

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="creative_url"
            type="url"
            fullWidth
            component={renderInputField}
            label={t(
              'services::forms.register_account.facebook-high-risk.fields.creative_url.label',
            )}
            t={t}
          />
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="cloaking_url"
            type="url"
            fullWidth
            component={renderInputField}
            label={t(
              'services::forms.register_account.facebook-high-risk.fields.cloaking_url.label',
            )}
            t={t}
          />
        </div>

        {currentProvider?.is_fan_page_id_required && (
          <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
            <Field
              normalize={onlyNumbers}
              name="fan_page_id"
              type="text"
              fullWidth
              component={renderInputField}
              label={t(
                'services::forms.register_account.facebook-high-risk.fields.fan_page_id.label',
              )}
              t={t}
            />
          </div>
        )}

        {currentProvider?.is_business_manager_id_required && (
          <div className={'margin-bottom-16'}>
            <Field
              normalize={onlyNumbers}
              name="business_manager_id"
              type="text"
              fullWidth
              component={renderInputField}
              label={t(
                'services::forms.register_account.facebook-high-risk.fields.business_manager_id.label',
              )}
              t={t}
            />
          </div>
        )}

        {currentProvider && <ProviderNotification currentProvider={currentProvider} />}

    </FormFrame>
  )
}

CreateFacebookHighRiskForm = reduxForm({
  form: 'CreateFacebookHighRiskAccountForm',
  initialValues: {
    email: '',
    login: '',
    network: '',
    provider_id: '',
  },
  shouldAsyncValidate: shouldAsyncValidate,
})(CreateFacebookHighRiskForm)

CreateFacebookHighRiskForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  onClose: PropTypes.func,
  valid: PropTypes.bool,
  autofill: PropTypes.func,
  form: PropTypes.string,
  networkId: PropTypes.string,
  multiLogins: PropTypes.bool,
  array: PropTypes.object,
  networkType: PropTypes.string,
}

export default CreateFacebookHighRiskForm
